import { useState } from 'react'
import { Box, Card, CardContent, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Delete as DeleteIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { exercisesApi } from '@/api/exercises'
import { EditorJsField } from '@/components/editor/EditorJsField'
import {
  ExercisePickerDialog,
  type PickerExercise,
} from '@/components/homeworks/ExercisePickerDialog'
import { emptyHomeworkItem, exerciseHomeworkItem, type EditableHomeworkItem } from '@/utils/homework'
import type { EditorJsData } from '@/types'

interface Props {
  items: EditableHomeworkItem[]
  onChange: (items: EditableHomeworkItem[]) => void
  /** Урок домашки — из него берутся темы программы для пикера заданий */
  lessonId: number
  disabled?: boolean
}

/**
 * Редактор блоков домашки.
 *
 * Домашка из одного текстового блока выглядит как один редактор — привычный
 * сценарий «задать домашку просто текстом» не должен требовать лишних кликов.
 * Перестановка стрелками, а не перетаскиванием: блоков единицы.
 */
export function HomeworkItemsEditor({ items, onChange, lessonId, disabled = false }: Props) {
  const { t } = useTranslation()
  const [pickerOpen, setPickerOpen] = useState(false)
  // «Один редактор во всю ширину» — только для единственного текстового блока
  const single = items.length === 1 && items[0]?.type === 'text'
  // Нумерация и стрелки нужны, только когда блоков больше одного
  const ordered = items.length > 1

  const updateAt = (index: number, task_json: EditorJsData) => {
    onChange(items.map((item, i) => (i === index ? { ...item, task_json } : item)))
  }

  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= items.length) return

    const next = [...items]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    onChange(next)
  }

  return (
    <Box>
      {items.map((item, index) => (
        <Box key={item.key} sx={{ mb: 2 }}>
          {/* Шапка есть у любого блока, даже единственного: список стартует
              пустым, блок добавлен руками — и удалить его должно быть чем */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {ordered ? t('homeworks.itemNumber', 'Блок {{n}}', { n: index + 1 }) : ''}
            </Typography>
            {ordered && (
              <>
                <Tooltip title={t('homeworks.moveUp', 'Выше')}>
                  <span>
                    <IconButton size="small" disabled={disabled || index === 0} onClick={() => move(index, -1)}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('homeworks.moveDown', 'Ниже')}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled || index === items.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Tooltip title={t('homeworks.removeItem', 'Удалить блок')}>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => onChange(items.filter((_, i) => i !== index))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {item.type === 'exercise' ? (
            // У блока-задания своего текста нет: ученик решает само задание
            <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExtensionIcon color="action" />
              <Typography sx={{ flex: 1 }}>
                {item.exercise_title ?? t('homeworks.exerciseItem', 'Интерактивное задание')}
              </Typography>
              <Chip size="small" label={t('homeworks.exerciseItemChip', 'Задание')} />
            </Paper>
          ) : (
            <EditorJsField
              key={`hw-item-${item.key}`}
              value={item.task_json}
              onChange={(next) => updateAt(index, next)}
              readOnly={disabled}
              extendedBlocks
              // Картинки в домашке идут в место учителя — тем же входом, что и
              // картинки заданий: домашку задаёт он, ему за неё и платить
              imageUploader={disabled ? undefined : (file) => exercisesApi.uploadImage(file)}
              hideLabel
              label=""
              rows={single ? 8 : 5}
              containerSx={{ mt: 0 }}
              initDelayMs={80}
            />
          )}
        </Box>
      ))}

      {/* Контурные блоки-приглашения вместо кнопок: домашка стартует пустой, и
          добавление блока — такое же действие, как добавление темы в программе.
          disabled у Card нет — гасим руками, иначе клик пройдёт при сохранении */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > *': { flex: 1 },
        }}
      >
        <Card
          variant="outlined"
          onClick={() => {
            if (!disabled) onChange([...items, emptyHomeworkItem()])
          }}
          sx={{
            borderStyle: 'dashed',
            cursor: disabled ? 'default' : 'pointer',
            pointerEvents: disabled ? 'none' : 'auto',
            opacity: disabled ? 0.5 : 1,
            bgcolor: 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <CardContent
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 2 }}
          >
            <AddIcon fontSize="small" color="action" />
            <Typography color="text.secondary">{t('homeworks.addItem', 'Добавить блок')}</Typography>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          onClick={() => {
            if (!disabled) setPickerOpen(true)
          }}
          sx={{
            borderStyle: 'dashed',
            cursor: disabled ? 'default' : 'pointer',
            pointerEvents: disabled ? 'none' : 'auto',
            opacity: disabled ? 0.5 : 1,
            bgcolor: 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <CardContent
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 2 }}
          >
            <ExtensionIcon fontSize="small" color="action" />
            <Typography color="text.secondary">
              {t('homeworks.addExerciseItem', 'Добавить интерактивное задание')}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <ExercisePickerDialog
        open={pickerOpen}
        lessonId={lessonId}
        onClose={() => setPickerOpen(false)}
        // Из пикера приходит усечённый ресурс: в блок домашки нужны ссылка на
        // задание, его название и слот выдачи — содержимое подтянет прогон.
        // Слот обязателен: одно задание стоит в нескольких темах программы, и
        // без него сервер угадает место работы и испортит статистику темы
        onPick={(exercise: PickerExercise) =>
          onChange([
            ...items,
            exerciseHomeworkItem(exercise.id, exercise.title, exercise.assigned_element_id),
          ])
        }
      />
    </Box>
  )
}
